// The identity review queue, as a contract rather than as three object
// literals inside a handler.
//
// This is the most dangerous surface in the product — answering one of these
// merges two people's careers or splits one person's in half — and until
// 2026-09-17 it was the surface with no written shape at all: the endpoint
// built anonymous objects, the admin page re-typed them, and neither could
// tell the other it had changed (audit 3.3, 3.4).

import { z } from 'zod';
import { pageOf } from './envelope';

/** A person the queue might be talking about, with the keys that name them. */
export const ReviewCandidate = z.object({
  id: z.string(),
  display: z.string(),
  type: z.string(),
  /** `{ gsis: '00-0033873', pfr: 'MahoPa00' }` — the crosswalk, as it stands. */
  keys: z.record(z.string(), z.string()),
});
export type ReviewCandidate = z.infer<typeof ReviewCandidate>;

/**
 * One unanswered question: a source named somebody we could not place.
 *
 * `proposed` is the extractor's own payload, left as it arrived — it is
 * whatever that source says a person is, and normalizing it here would hide
 * exactly the detail somebody is deciding on.
 */
export const EntityReview = z.object({
  id: z.string(),
  sourceId: z.string(),
  namespace: z.string(),
  key: z.string(),
  proposed: z.unknown(),
  candidateIds: z.array(z.string()),
  observedAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedTo: z.string().nullable(),
});
export type EntityReview = z.infer<typeof EntityReview>;

/**
 * `GET /api/admin/reviews`.
 *
 * A `Page` like every other list, plus the candidates hydrated by id —
 * because a decision made against opaque ids is a decision made blind, and
 * because the same candidate turns up under several reviews.
 */
export const ReviewQueueView = pageOf(EntityReview).extend({
  candidates: z.record(z.string(), ReviewCandidate),
});
export type ReviewQueueView = z.infer<typeof ReviewQueueView>;

/**
 * `POST /api/admin/reviews` — answering one.
 *
 * A discriminated union, because `entityId` is meaningful on exactly one of
 * the two answers and a flat shape lets a caller send it with the other.
 * 'same' merges this key into an existing person; 'different' mints a new one.
 */
export const ResolveReviewRequest = z.discriminatedUnion('decision', [
  z.object({
    id: z.string().min(1),
    decision: z.literal('same'),
    entityId: z.string().min(1, 'Deciding "same" needs the person it is the same as.'),
  }),
  z.object({
    id: z.string().min(1),
    decision: z.literal('different'),
  }),
]);
export type ResolveReviewRequest = z.infer<typeof ResolveReviewRequest>;

export const ReviewResolution = z.object({
  id: z.string(),
  decision: z.enum(['same', 'different']),
  resolvedTo: z.string(),
  /** What happens next, in a sentence, because this is a one-way door. */
  note: z.string(),
});
export type ReviewResolution = z.infer<typeof ReviewResolution>;

// ── The ingest dashboard (scenario O1) ─────────────────────────────────────
//
// What the ingest worker did, read from what it already writes: the run
// ledger (`ingest_runs`), the clock's heartbeat, the week's games and stat
// lines, the identity review queue and the words nothing maps yet. Nothing
// here is stored for the dashboard's sake (PLAN-2026-10-09-ingest-dashboard).

/**
 * How a run ended, in the ledger's own words (migration 0005): `failed` and
 * `refused` are an alarm, `partial` is a queue for a person, `ok` and `idle`
 * are fine, `running` is not finished.
 */
export const IngestRunStatus = z.enum(['running', 'ok', 'idle', 'partial', 'refused', 'failed']);
export type IngestRunStatus = z.infer<typeof IngestRunStatus>;

/** One row of the run ledger. */
export const IngestRun = z.object({
  id: z.string(),
  sourceId: z.string(),
  /** The file with its partition, when it has one: `stats_player/stats_player_week#2026`. */
  resource: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  status: IngestRunStatus,
  /** What the run stored — facts, lines, rows changed. Null when the run never said. */
  rowsWritten: z.number().int().nullable(),
  /** The worker's own sentence about the run. */
  note: z.string().nullable(),
  /** What the run cost the database (migration 0044). Null when it was not metered. */
  rowsRead: z.number().int().nullable(),
  d1RowsWritten: z.number().int().nullable(),
});
export type IngestRun = z.infer<typeof IngestRun>;

/**
 * One thing that needs the admin, said in words.
 *
 * `failing`: a file whose last two runs both failed or were refused — a single
 * failure is retried by the next pass and shows only on its row. `late`: a
 * file that runs every pass has not run in twice its usual gap. `clock`: the
 * minute sweep's heartbeat has stopped. `reviews`: people wait in the identity
 * review queue. `unmapped`: a source used a word, first seen this week, that
 * nothing maps yet.
 */
export const IngestNeed = z.object({
  kind: z.enum(['failing', 'late', 'clock', 'reviews', 'unmapped']),
  /** The source it is about; null for one about no source (the review queue). */
  sourceId: z.string().nullable(),
  /** The file it is about, without a partition; null when it is about no one file. */
  family: z.string().nullable(),
  /**
   * The instant the need counts from: a failing file's first failure in the
   * streak, a late file's last run (the newest of a source's late files), the
   * clock's last sweep, a new word's first sighting; null for the review queue
   * and for a clock that never swept.
   */
  since: z.string().nullable(),
  /** What happened, in a sentence. */
  says: z.string(),
  /** What to do about it, in a sentence. */
  next: z.string(),
});
export type IngestNeed = z.infer<typeof IngestNeed>;

/** Runs in the window, by how they ended. */
export const IngestCounts = z.object({
  running: z.number().int().min(0),
  ok: z.number().int().min(0),
  idle: z.number().int().min(0),
  partial: z.number().int().min(0),
  refused: z.number().int().min(0),
  failed: z.number().int().min(0),
});
export type IngestCounts = z.infer<typeof IngestCounts>;

/**
 * One file a source publishes, every partition of it together: a season's
 * stat file is one row with its seasons in it, the weather is one row with
 * its games in it.
 */
export const IngestSource = z.object({
  sourceId: z.string(),
  /** The source's own name, as it registered itself; `The site` for the site's own steps. */
  sourceName: z.string(),
  /** The file without its partition: `stats_player/stats_player_week`, `points`. */
  family: z.string(),
  /** The site's own steps (`fsp`) run after the files they are made from, not on a cadence. */
  own: z.boolean(),
  /**
   * False for a file switched off on purpose (`source_resource.enabled = 0`):
   * the worker refuses it on every pass, which is the switch working, so it
   * is never failing or late and has no trigger.
   */
  enabled: z.boolean(),
  /** How many partitions ran in the window — seasons, stadiums, games. */
  partitions: z.number().int().min(0),
  /** The newest run, in the window or before it; null when it has never run. */
  newest: IngestRun.nullable(),
  /** When a run last stored something; null when none in the window did. */
  lastChangedAt: z.string().nullable(),
  counts: IngestCounts,
  /**
   * When the next run is due at the latest, for a file that runs every pass
   * (`daily-rebuild` and `continuous` files of a source with a cadence); null
   * for one that runs only when there is something to fetch, and for one that
   * has never run, which the worker has no reader for yet.
   */
  dueBy: z.string().nullable(),
  late: z.boolean(),
  /** Runs failed or refused in a row, newest first, in the partition with the most. */
  failingInARow: z.number().int().min(0),
  /**
   * What the worker's `?resource=` takes to run this file now, or null when it
   * cannot be run alone: the site's own steps run after their inputs.
   */
  trigger: z.string().nullable(),
});
export type IngestSource = z.infer<typeof IngestSource>;

/** One week of a sport's season, as its games and stat lines stand. */
export const IngestWeek = z.object({
  sport: z.string(),
  season: z.number().int(),
  period: z.number().int(),
  label: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  games: z.number().int().min(0),
  final: z.number().int().min(0),
  /** Final games with their stat lines in. */
  withLines: z.number().int().min(0),
  /** Line versions written after a line first landed: stat corrections. */
  revisions: z.number().int().min(0),
  /** When a line in the week was last written; null when none has been. */
  lastLandedAt: z.string().nullable(),
  /**
   * `upcoming`: no game has started. `under-way`: games are still to finish.
   * `waiting`: every game is final and some have no lines yet. `complete`:
   * every game is final and has its lines.
   */
  state: z.enum(['upcoming', 'under-way', 'waiting', 'complete']),
});
export type IngestWeek = z.infer<typeof IngestWeek>;

/** A word a source used that nothing maps yet (`concept_map`). */
export const UnmappedWord = z.object({
  sourceId: z.string(),
  /** What kind of word: `position`, `practice-status`. */
  domain: z.string(),
  value: z.string(),
  firstSeenAt: z.string(),
});
export type UnmappedWord = z.infer<typeof UnmappedWord>;

/** `GET /api/admin/ingest`. */
export const IngestDashboardView = z.object({
  /** The instant it was read at. */
  at: z.string(),
  /** How many days back the sources' counts look. */
  windowDays: z.number().int().positive(),
  /** When the minute sweep last started; null when it never has. */
  sweptAt: z.string().nullable(),
  /** Most urgent first; empty on a good day. */
  needs: z.array(IngestNeed),
  /** By source, the providers' files first and the site's own steps last. */
  sources: z.array(IngestSource),
  /** The newest season of each sport with a schedule, week by week. */
  weeks: z.array(IngestWeek),
  reviewsOpen: z.number().int().min(0),
  /** Every word nothing maps yet, newest first; the ones first seen this week are also needs. */
  unmapped: z.array(UnmappedWord),
});
export type IngestDashboardView = z.infer<typeof IngestDashboardView>;

/** `GET /api/admin/ingest/runs` — one file's runs, newest first. */
export const IngestRunsView = pageOf(IngestRun);
export type IngestRunsView = z.infer<typeof IngestRunsView>;
